import React, { type JSX } from 'react';

import { isEmptyFragment, nodeHasRenderableContent } from '../../util/elements';
import { type StackProps } from '../structural/Stack';

import { ExplicitActions } from './ExplicitActions';

/**
 * Props for the {@link Actions} component that are applicable when the actions in the
 * {@link Actions} component are being dynamically rendered based on non-JSX data structures.
 *
 * In this case, the actions, {@link T[]}, must be provided explicitly via the 'actions', prop, and
 * the 'children' prop is used to control the rendering of each action, {@link T}.
 */
export interface RenderPropActionsProps<T> extends Omit<StackProps, 'children' | 'direction'> {
  readonly actions?: (null | T)[];
  readonly children: (action: T) => JSX.Element | null;
}

export const RenderPropActions = <T,>({
  actions = [],
  children,
  ...props
}: RenderPropActionsProps<T>): JSX.Element => (
  <ExplicitActions {...props}>
    {actions.reduce((acc, action, index) => {
      if (action !== null) {
        const element = children(action);
        if (element && !isEmptyFragment(element) && nodeHasRenderableContent(element)) {
          return [...acc, <React.Fragment key={index}>{element}</React.Fragment>];
        }
      }
      return acc;
    }, [] as JSX.Element[])}
  </ExplicitActions>
);
